import React, { useState, useEffect } from 'react'
import { CSSTransition } from 'react-transition-group'

import { growthbookFeatures } from 'utils/growthbook'

import { useGrowthBookFeature } from '../../../context/growthbook-context'
import { fonts, palette } from '../../../styles/variables'
import { ReactComponent as OrangeTick } from '../../../styles/assets/checkoutModal/tick-orange.svg'
import { Text } from '../../topography'
import { translate } from '../../../utils/translation'
import { LoadingWheel } from '../../loading/Loading'
import { LimitedOffer } from '../../limitedOffer'
import { formatNumber } from '../../../utils/numbers'
import { RadioTick } from '../../../containers/Checkout/styled'
import { PROMO_LABEL_VALUE } from '../../../utils/consts.mjs'

import {
  PlansContainer,
  PlanItem,
  PlanRadioButton,
  PlanContentWrapper,
  PriceContainer,
  PriceDisplay,
  PriceValueContainer,
  BenefitIndicator,
  PlanDescriptionWrapper,
  PlanDescriptionText,
  PlanBadgeWrapper,
} from './styled'

const CheckoutPlanSelector = ({
  selected,
  onClick,
  options,
  name,
  isLoading,
}) => {
  const { isEnabled: isGrowthbookFeatureOn, theme: featureTheme } =
    useGrowthBookFeature(growthbookFeatures.PROMO_OFFER)
  const [bannerTheme, setBannerTheme] = useState(
    featureTheme?.promotionLabel || null,
  )

  useEffect(() => {
    if (featureTheme?.promotionLabel && !bannerTheme) {
      setBannerTheme(featureTheme.promotionLabel)
    }
  }, [featureTheme?.promotionLabel])

  return (
    <PlansContainer length={options?.length}>
      {options.map((item) => {
        const {
          monthlyPrice,
          unitPrice,
          planLabel,
          region,
          tier,
          subscriptionDescription,
          // TODO: uncomment when coupons will be ready
          // discountPercent = null,
          benefit,
          limitedOffer,
          hideLimitedBadge,
          theme,
          previousePrice,
          checkoutLabel,
        } = item
        const [type, planName] = translate(
          `dropdown_${tier}_${checkoutLabel || planLabel}`,
        ).split(' - ')
        const yearlyPlanMonthlyPrice = `${formatNumber(
          monthlyPrice,
          true,
          2,
          region,
          false,
        )}${translate('slash_month')}`

        const selectedItem = selected?.planLabel === planLabel

        return (
          <PlanItem
            key={planLabel}
            limitedOffer={limitedOffer}
            theme={theme}
            selected={selectedItem}
          >
            <CSSTransition
              in={selectedItem}
              timeout={200}
              classNames="fade"
              unmountOnExit
            >
              <RadioTick>
                <OrangeTick
                  style={{
                    width: '22px',
                    height: '22px',
                  }}
                />
              </RadioTick>
            </CSSTransition>
            {limitedOffer && (
              <PlanBadgeWrapper background={bannerTheme?.background}>
                <Text
                  fontFamily={fonts.primaryBook}
                  color={bannerTheme?.color || palette.white}
                  fontSize="12px"
                  fontWeight={500}
                  textTransform="uppercase"
                  textAlign="center"
                  whiteSpace="nowrap"
                >
                  {translate(limitedOffer)}
                </Text>
              </PlanBadgeWrapper>
            )}
            {limitedOffer && !hideLimitedBadge && (
              <LimitedOffer
                styles={{
                  position: 'absolute',
                  right: '0',
                  top: '28px',
                  transform: 'translate(20px, 0)',
                  zIndex: 10,
                }}
              />
            )}
            <PlanRadioButton
              type="radio"
              value={planLabel}
              name={name}
              checked={selectedItem}
              limitedOffer={limitedOffer}
              onChange={() => onClick(item)}
              theme={theme}
            />
            <PlanContentWrapper limitedOffer={limitedOffer}>
              {/* TODO: uncomment when coupons will be ready */}
              {/* {discountPercent && (
                <DiscountIndicator className="discount" isLoading={isLoading}>
                  <Text
                    fontFamily={fonts.primaryMedium}
                    fontSize="12px"
                    textAlign="center"
                    margin="10px 0 0 0"
                    transform="translateY(-4px)"
                  >
                    {discountPercent}
                  </Text>
                </DiscountIndicator>
              )} */}
              <PriceContainer
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <PriceDisplay>
                  <Text
                    className="plan-name"
                    fontSize="20px"
                    fontFamily={fonts.primaryMedium}
                    transition="color.3s ease"
                    textTransform="capitalize"
                    textAlign="center"
                  >
                    {planName || type}
                  </Text>
                  {planName && (
                    <Text fontSize="16px" margin="16px 0 0 0">
                      {type}
                    </Text>
                  )}
                  <PriceValueContainer className="price-value">
                    {previousePrice && (
                      <div
                        style={{
                          width: '100%',
                          textAlign: 'center',
                          marginBottom: '8px',
                        }}
                      >
                        <Text
                          fontSize="14px"
                          transform="translateY(2px)"
                          wordBreak="keep-all"
                          textDecoration="line-through"
                          color={palette.darkGray70}
                          textAlign="center"
                        >
                          {previousePrice}
                        </Text>
                      </div>
                    )}
                    {isLoading ? (
                      <LoadingWheel width={24} height={24} />
                    ) : (
                      <Text
                        fontSize="24px"
                        fontFamily={fonts.primaryMedium}
                        transform="translateY(2px)"
                        wordBreak="keep-all"
                      >
                        {unitPrice}
                      </Text>
                    )}
                    {benefit && !isGrowthbookFeatureOn && (
                      <BenefitIndicator
                        className="plan-benefit"
                        isLoading={isLoading}
                      >
                        {benefit}
                      </BenefitIndicator>
                    )}
                  </PriceValueContainer>
                </PriceDisplay>
                {!isLoading &&
                  (planLabel === 'yearly' ||
                    planLabel === PROMO_LABEL_VALUE) && (
                    <Text
                      color={palette.orange}
                      margin="8px 0 0 0"
                      textAlign="center"
                      dangerouslySetInnerHTML={{
                        __html: yearlyPlanMonthlyPrice,
                      }}
                    />
                  )}
                <PlanDescriptionWrapper>
                  {subscriptionDescription && (
                    <PlanDescriptionText
                      theme={theme}
                      selected={selected?.planLabel === planLabel}
                      isLoading={isLoading}
                      dangerouslySetInnerHTML={{
                        __html: subscriptionDescription,
                      }}
                    />
                  )}
                </PlanDescriptionWrapper>
              </PriceContainer>
            </PlanContentWrapper>
          </PlanItem>
        )
      })}
    </PlansContainer>
  )
}

export default CheckoutPlanSelector
